import { useEffect, useRef, useState } from 'react';
import { CloseIcon, SearchIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { PRODUCT_PICK_MIN_LENGTH } from '@/pages/fulfillment/components/productPick';
import type { ProductPick } from '@/pages/fulfillment/components/productPick';
import { isOversold, shelfLabel, skusNamedBy } from '../inventoryView';
import type { InventoryScreenRow } from '../inventoryView';

/** A product the counter can add to the shelf: enough to recognise the box and to file it by sku. */
export interface ShelfCandidate {
  sku: string;
  brand: string | null;
  name: string | null;
  nameKor: string | null;
  size: string | null;
  barcode: string | null;
  imageUrl: string | null;
  /** Where the book has it now, when it has it anywhere, so the counter can tell a misplaced box from a new one. */
  elsewhere: string[];
}

export interface FullCountProductPanelProps {
  /** The code scanned that names no row of the shelf; blank when the panel was opened to search by name. */
  code: string;
  shelf: string;
  /** Every stock row on screen: a product the book holds on another shelf is offered from here first. */
  rows: InventoryScreenRow[];
  /** Reads the code against the catalog (sku, then barcode); answers the sku it names, or null. */
  lookupCode: (code: string) => Promise<string | null>;
  /** The catalog details of one sku, for a product the book holds nowhere. */
  productOf: (sku: string) => Promise<ShelfCandidate | null>;
  /** The shop's own search, by name, brand or sku. */
  search: (term: string) => Promise<ProductPick[]>;
  busy: boolean;
  onAdd: (candidate: ShelfCandidate) => void;
  onClose: () => void;
}

const fromRow = (row: InventoryScreenRow, rows: InventoryScreenRow[]): ShelfCandidate => ({
  sku: row.sku,
  brand: row.brand ?? null,
  name: row.name ?? null,
  nameKor: row.nameKor ?? null,
  size: row.size ?? null,
  barcode: row.barcode ?? null,
  imageUrl: row.imageUrl ?? null,
  elsewhere: rows.filter((entry) => entry.sku === row.sku && entry.onHand > 0).map((entry) => entry.locationCode),
});

const fromPick = (pick: ProductPick, rows: InventoryScreenRow[]): ShelfCandidate => {
  const stocked = rows.find((row) => row.sku === pick.sku);
  return {
    sku: pick.sku,
    brand: pick.brand || null,
    name: pick.name || null,
    nameKor: pick.nameKor,
    size: pick.size,
    barcode: stocked?.barcode ?? null,
    imageUrl: pick.imageUrl,
    elsewhere: rows.filter((row) => row.sku === pick.sku && row.onHand > 0).map((row) => row.locationCode),
  };
};

/**
 * Below the full count band when a scanned code names no row of the shelf [SSD-7662]: the products the code names (the
 * book's own rows elsewhere first, then the catalog), and a catalog search by name for a box with no readable code.
 * Adding one opens a row at 0 on the shelf for the counter to count; nothing is created in the catalog.
 */
export function FullCountProductPanel({
  code,
  shelf,
  rows,
  lookupCode,
  productOf,
  search,
  busy,
  onAdd,
  onClose,
}: FullCountProductPanelProps) {
  const [named, setNamed] = useState<ShelfCandidate[] | null>(null);
  const [namedFailed, setNamedFailed] = useState(false);
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<ShelfCandidate[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    setNamed(null);
    setNamedFailed(false);
    setFound(null);
    setTerm('');
    if (!code.trim()) {
      setNamed([]);
      return;
    }
    // The book's own rows first: a box on the wrong shelf is the usual reason a code names no row here.
    const stocked = skusNamedBy(code, rows)
      .map(
        (sku) =>
          rows.find((row) => row.sku === sku && row.locationCode !== shelf) ?? rows.find((row) => row.sku === sku),
      )
      .filter((row): row is InventoryScreenRow => row !== undefined)
      .map((row) => fromRow(row, rows));
    if (stocked.length > 0) {
      setNamed(stocked);
      return;
    }
    void (async () => {
      try {
        const sku = await lookupCode(code.trim());
        const product = sku ? await productOf(sku) : null;
        if (request === requestRef.current) {
          setNamed(product ? [{ ...product, elsewhere: [] }] : []);
        }
      } catch {
        if (request === requestRef.current) {
          setNamed([]);
          setNamedFailed(true);
        }
      }
    })();
    // The panel reads the code once per scan; the rows moving under it every 20 seconds must not re-run the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, shelf]);

  const runSearch = async () => {
    const typed = term.trim();
    if (typed.length < PRODUCT_PICK_MIN_LENGTH) {
      return;
    }
    const request = ++requestRef.current;
    setSearching(true);
    setSearchFailed(false);
    try {
      const picks = await search(typed);
      if (request === requestRef.current) {
        setFound(picks.map((pick) => fromPick(pick, rows)));
      }
    } catch {
      if (request === requestRef.current) {
        setFound([]);
        setSearchFailed(true);
      }
    } finally {
      if (request === requestRef.current) {
        setSearching(false);
      }
    }
  };

  /** A row of this sku on this shelf whose book does not add up sits the walk out, so it is not added from here either. */
  const blockedHere = (sku: string): boolean =>
    rows.some((row) => row.sku === sku && row.locationCode === shelf && isOversold(row));

  const card = (candidate: ShelfCandidate) => (
    <li key={candidate.sku} className="fc-cand">
      {candidate.imageUrl ? (
        <img className="thumb" src={candidate.imageUrl} alt="" />
      ) : (
        <div className="thumb">IMG</div>
      )}
      <div className="fc-cand-txt">
        <div className="fc-cand-name">
          {candidate.brand && <b>{candidate.brand} </b>}
          {candidate.name ?? candidate.sku}
        </div>
        <div className="fc-cand-meta">
          {[candidate.sku, candidate.nameKor, candidate.size, candidate.barcode].filter(Boolean).join(' · ')}
        </div>
        {candidate.elsewhere.length > 0 && (
          <div className="fc-cand-meta">{`The book has it on ${candidate.elsewhere.map(shelfLabel).join(', ')}`}</div>
        )}
      </div>
      {blockedHere(candidate.sku) && (
        <span className="fc-cand-meta">Reserved above stock on this shelf: repair it under Needs fixing first.</span>
      )}
      <button
        type="button"
        className="btn btn-blue btn-sm"
        disabled={busy || blockedHere(candidate.sku)}
        aria-label={`Add ${candidate.sku} to ${shelfLabel(shelf)}`}
        onClick={() => onAdd(candidate)}
      >
        {`Add to ${shelfLabel(shelf)}`}
      </button>
    </li>
  );

  return (
    <section className="fc-find" aria-label="Find a product to add to this shelf">
      <div className="fc-find-head">
        <span>
          {code.trim()
            ? `${code.trim()} names no row on ${shelfLabel(shelf)}.`
            : `Find a product to add to ${shelfLabel(shelf)}.`}{' '}
          Adding opens a row at 0 for you to count.
        </span>
        <span className="sp" />
        <button type="button" className="btn btn-line btn-sm" aria-label="Close product search" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      {code.trim() !== '' && (
        <>
          {named === null && <p className="blocked-note">Looking the code up…</p>}
          {named !== null && named.length > 0 && <ul className="fc-cands">{named.map(card)}</ul>}
          {named !== null && named.length === 0 && (
            <p className="blocked-note">
              {namedFailed
                ? 'The code could not be looked up right now. Search by name below.'
                : 'The catalog has no product with this code. Search by name or size below; nothing new is created.'}
            </p>
          )}
        </>
      )}
      <div className="fc-find-search">
        <input
          className="inp"
          aria-label="Search the catalog by name, brand or SKU"
          placeholder="Name, brand or SKU"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void runSearch();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-line btn-sm"
          disabled={searching || term.trim().length < PRODUCT_PICK_MIN_LENGTH}
          onClick={() => void runSearch()}
        >
          <SearchIcon />
          Search
        </button>
      </div>
      {searching && <p className="blocked-note">Searching…</p>}
      {!searching && found !== null && found.length > 0 && <ul className="fc-cands">{found.map(card)}</ul>}
      {!searching && found !== null && found.length === 0 && (
        <p className="blocked-note">
          {searchFailed
            ? 'The catalog could not be searched right now. Try again shortly.'
            : 'Nothing matches. Try the name or size again.'}
        </p>
      )}
    </section>
  );
}
